import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Bookmark, Users, ArrowUpRight } from "lucide-react";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function HiringListsPage() {
    const [lists, setLists] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", description: "", color: "#002FA7" });

    const load = async () => {
        try {
            const { data } = await api.get("/hiring-lists");
            setLists(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/hiring-lists", form);
            toast.success("List created");
            setOpen(false);
            setForm({ name: "", description: "", color: "#002FA7" });
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-8">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Hiring lists</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Custom candidate lists</h1>
                    <p className="text-muted-foreground mt-2">Group candidates for future roles, talent pools, alumni networks, or interview tracks.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="new-list-btn"><Plus className="w-4 h-4 mr-2" /> New list</Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none">
                        <DialogHeader>
                            <DialogTitle className="font-display font-bold text-2xl">New hiring list</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Name</Label>
                                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-2 h-11" placeholder="Future Senior Hires" data-testid="list-form-name" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Description</Label>
                                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-2" data-testid="list-form-desc" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Color</Label>
                                <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="rounded-none mt-2 h-11 w-20" data-testid="list-form-color" />
                            </div>
                            <DialogFooter>
                                <Button type="submit" className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="list-form-submit">Create list</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {lists.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center">
                    <Bookmark className="w-8 h-8 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-display font-bold text-xl">No lists yet</h3>
                    <p className="text-sm text-muted-foreground mt-2">Create your first list to organise candidates beyond job openings.</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                    {lists.map((l) => (
                        <Link key={l.id} to={`/lists/${l.id}`} className="bg-white p-6 hover:bg-secondary transition-colors group" data-testid={`list-card-${l.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="inline-flex items-center gap-2">
                                    <span className="w-3 h-3" style={{ backgroundColor: l.color || "#002FA7" }} />
                                    <h3 className="font-display font-bold text-lg leading-tight group-hover:text-ikb transition-colors">{l.name}</h3>
                                </div>
                                <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-ikb" />
                            </div>
                            {l.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{l.description}</p>}
                            <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
                                <Users className="w-3 h-3" /> {l.member_count} candidate{l.member_count === 1 ? "" : "s"}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
